// Subida del binario del adjunto al almacenamiento. HU-07, paso 2 de 3.
//
// El binario NO pasa por la API (ADR-015): el tope de peticion es 1 MiB y el de
// adjunto 25 MiB, asi que un PDF de necropsia no cabria. El flujo es:
//
//   1. `POST /casos/{id}/adjuntos/url-de-subida` -> URL firmada, de un solo blob.
//   2. `PUT` del binario contra esa URL, directo al almacenamiento (esta funcion).
//   3. `POST /casos/{id}/adjuntos` -> se registra la referencia con su huella.
//
// El orden no es intercambiable: registrar la referencia antes de que la subida
// termine dejaria el caso con una evidencia que no existe, y el control C-01 —"toda
// sospecha esta registrada CON EVIDENCIAS ADJUNTAS"— la daria por sustento valido.

import { ErrorDeApi, pedir, segUuid } from './cliente';

/**
 * Paso 1. Pide permiso para subir UN archivo.
 *
 * `contieneDatoDePersonas` decide el CONTENEDOR, no un metadato: ADR-004 separa
 * `expedientes` (sin caducidad) de `pii-5a` (5 anios). Marcarlo mal no da error hoy;
 * lo da en el ano 5, cuando el dato de una persona resulte imposible de borrar.
 */
/** Lo que el servidor devuelve al firmar la subida: donde poner el binario y con que nombre. */
export type PermisoDeSubida = {
  url: string;
  ruta_blob: string;
  expira_en?: string;
};

type DatosDelPermiso = {
  nombreArchivo: string;
  huellaSha256: string;
  tamanoBytes: number;
  /** Marca el destino de retencion: `pii-5a` en vez de `expedientes`. Ver el comentario de arriba. */
  contieneDatoDePersonas?: boolean;
};

export function pedirPermisoDeSubida(
  casoId: string,
  { nombreArchivo, huellaSha256, tamanoBytes, contieneDatoDePersonas = false }: DatosDelPermiso,
) {
  return pedir<PermisoDeSubida>(`/casos/${segUuid(casoId)}/adjuntos/url-de-subida`, {
    metodo: 'POST',
    cuerpo: {
      nombre_archivo: nombreArchivo,
      // LA HUELLA Y EL TAMANIO VIAJAN YA EN ESTE PASO, y son obligatorios: el servidor
      // aplica con ellos los limites de HU-31 **antes** de firmar el permiso. Validar
      // despues de haber firmado dejaria el binario dentro del contenedor aunque el
      // registro se rechace.
      //
      // Faltaban, y el sintoma era un 422 "Hay datos que el servidor no acepta" al
      // pulsar Adjuntar — sin llegar a tocar Azure (26-ago-2026).
      huella_sha256: huellaSha256,
      tamano_bytes: tamanoBytes,
      contiene_dato_de_personas: contieneDatoDePersonas,
    },
  });
}

/**
 * Paso 2. Sube el binario contra la URL firmada.
 *
 * No pasa por `pedir()` a proposito: eso antepone `/api` y las cabeceras de la API,
 * y esto va a otro host —el almacenamiento— con las cabeceras que Azure Blob exige.
 */
export async function subirBinario(archivo: File, permiso: PermisoDeSubida) {
  let respuesta: Response;
  try {
    respuesta = await fetch(permiso.url, {
      method: 'PUT',
      headers: {
        // Obligatoria en Blob Storage: sin ella el PUT se rechaza con 400.
        'x-ms-blob-type': 'BlockBlob',
        'Content-Type': archivo.type || 'application/octet-stream',
      },
      body: archivo,
    });
  } catch {
    throw new ErrorDeApi(
      'No se pudo subir el archivo al almacenamiento. Revisa tu conexión e inténtalo de nuevo.',
      { estado: 0, cuerpo: null },
    );
  }

  if (!respuesta.ok) {
    // El caso mas probable en la practica: la URL firmada caduco mientras el usuario
    // elegia el archivo. Se distingue para poder pedir una nueva en vez de decirle
    // que el archivo esta mal.
    if (respuesta.status === 403) {
      throw new ErrorDeApi(
        'El permiso de subida caducó. Vuelve a intentarlo: se pedirá uno nuevo.',
        { estado: 403, cuerpo: null },
      );
    }
    throw new ErrorDeApi(`El almacenamiento rechazó el archivo (${respuesta.status}).`, {
      estado: respuesta.status,
      cuerpo: null,
    });
  }

  return permiso.ruta_blob;
}

// ---- #377 · MG-ILE-377 · varios archivos en UNA petición (AT S3 §2.2.7, endpoint 51) ------

/** Un archivo de la selección: la huella y el tamaño ya calculados en el navegador. */
export type ArchivoDelLote = { nombre_archivo: string; huella_sha256: string; tamano_bytes: number };

/** El permiso de un archivo aceptado, con el nombre y la huella para emparejarlo sin deducirlo de la ruta. */
export type PermisoDelLote = PermisoDeSubida & { nombre_archivo: string; huella_sha256: string; contenedor?: string };

export type RespuestaDelLote = {
  aceptados: PermisoDelLote[];
  /** «setup.exe: no se aceptan ejecutables» (#377-C3), tal cual. */
  rechazados: { nombre_archivo: string; texto: string }[];
  /** «6 MB de 100 MB» (#377-C1). */
  texto_del_presupuesto: string;
};

/**
 * Paso 1 en lote. Todo o nada en el cupo: si la selección no cabe, 422 con el `detalle`
 * («Elegiste 4 archivos y solo caben 2 (máximo 8 por caso)») y no se emite ninguna URL (C2).
 * La marca de datos de personas vale para TODO el lote (C4).
 */
export function pedirPermisosDeSubidaEnLote(
  casoId: string,
  { contieneDatoDePersonas = false, archivos }: { contieneDatoDePersonas?: boolean; archivos: ArchivoDelLote[] },
) {
  return pedir<RespuestaDelLote>(`/casos/${segUuid(casoId)}/adjuntos/url-de-subida/lote`, {
    metodo: 'POST',
    cuerpo: { contiene_dato_de_personas: contieneDatoDePersonas, archivos },
  });
}
